import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Loader2, Check, ExternalLink, RefreshCw, PartyPopper } from "lucide-react";

import { api, formatApiError } from "@/lib/api";

const PLAN_DEFS = {
  artist: {
    name: "Artist",
    price: "£1.58",
    cadence: "/ month",
    billedNote: "Billed £19 annually",
    features: ["Unlimited releases", "90% royalties", "150+ stores & platforms", "Real-time analytics"],
    highlight: false,
  },
  pro: {
    name: "Pro",
    price: "£2.42",
    cadence: "/ month",
    billedNote: "Billed £29 annually",
    features: ["Unlimited releases", "95% royalties", "Music video → VEVO & Apple Music", "Advanced analytics", "Priority review"],
    highlight: true,
  },
  label: {
    name: "Label",
    price: "£8.25",
    cadence: "/ month",
    billedNote: "Billed £99 annually",
    features: ["Unlimited releases on your label", "100% royalties", "Multiple artists & team seats", "Royalty splits"],
    highlight: false,
  },
};

export default function BillingPanel({ tiers = ["artist", "pro"] }) {
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [justActivated, setJustActivated] = useState(false);
  // Guard so the URL-driven side-effects (?tier=, ?billing=return) fire exactly once
  // even if the parent re-renders and passes a fresh `tiers` array literal.
  const initRan = useRef(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/billing/subscription");
      setSub(data);
    } catch (e) {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, []);

  const upgrade = useCallback(async (tier) => {
    setBusy(tier);
    try {
      const { data } = await api.post("/billing/checkout", { tier, app_url: window.location.origin });
      // Off to Stripe-hosted checkout.
      window.location.assign(data.purchase_url);
    } catch (err) {
      toast.error(formatApiError(err, "Could not start checkout"));
      setBusy("");
    }
  }, []);

  useEffect(() => {
    if (initRan.current) return;
    initRan.current = true;
    (async () => {
      await load();
      const params = new URLSearchParams(window.location.search);

      // 1) Returning from a Stripe checkout — sync the subscription and celebrate.
      if (params.get("billing") === "return") {
        try {
          const { data } = await api.post("/billing/sync");
          setSub(data);
          if (data?.active) {
            setJustActivated(true);
            toast.success(`You're on the ${data.tier} plan`);
          } else {
            toast.info("Payment received — activation may take a moment. Tap Refresh shortly.");
          }
        } catch (e) {
          /* ignore */
        }
        params.delete("billing");
        params.delete("session_id");
        window.history.replaceState({}, "", window.location.pathname + (params.toString() ? `?${params}` : ""));
        // Clear the stored pending tier — the user has landed back and we sync'd.
        try { window.localStorage.removeItem("noizbloc_pending_tier"); } catch (e) { /* ignore */ }
        return;
      }

      // 2) User arrived from Landing → chose a tier → registered → tier param present.
      //    If that tier is allowed for this role and they aren't already subscribed to it,
      //    kick off the Stripe checkout immediately.
      const paramTier = params.get("tier");
      const stored = (() => { try { return window.localStorage.getItem("noizbloc_pending_tier") || ""; } catch (e) { return ""; } })();
      const rawTier = paramTier || stored;
      if (rawTier && tiers.includes(rawTier)) {
        params.delete("tier");
        window.history.replaceState({}, "", window.location.pathname + (params.toString() ? `?${params}` : ""));
        try { window.localStorage.removeItem("noizbloc_pending_tier"); } catch (e) { /* ignore */ }
        // Small delay so the Billing panel paints once before the browser leaves for Stripe.
        setTimeout(() => { upgrade(rawTier); }, 350);
      } else if (rawTier && !tiers.includes(rawTier)) {
        // Mis-matched tier (e.g. artist picked Label plan). Clear it and inform them.
        params.delete("tier");
        window.history.replaceState({}, "", window.location.pathname + (params.toString() ? `?${params}` : ""));
        try { window.localStorage.removeItem("noizbloc_pending_tier"); } catch (e) { /* ignore */ }
        toast.info(`The ${rawTier.toUpperCase()} plan is only available for that account type.`);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = async () => {
    setBusy("refresh");
    try {
      const { data } = await api.post("/billing/sync");
      setSub(data);
      toast.success(data?.active ? `Active: ${data.tier} plan` : "No active subscription found yet");
    } catch (e) {
      toast.error("Refresh failed");
    } finally {
      setBusy("");
    }
  };

  if (loading) return <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-slate-500" /></div>;

  const activeTier = sub?.active ? sub.tier : null;

  return (
    <div className="space-y-8" data-testid="billing-panel">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl tracking-tight text-white">Billing & plan</h2>
          <p className="mt-1.5 text-slate-400">Secure checkout by Stripe. Cancel or re-up anytime from the Billing tab.</p>
        </div>
        <button data-testid="billing-refresh" onClick={refresh} disabled={busy === "refresh"} className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2.5 text-sm text-white hover:bg-white/5 transition-colors disabled:opacity-60 self-start">
          {busy === "refresh" ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />} Refresh status
        </button>
      </div>

      {justActivated && (
        <div
          data-testid="billing-just-activated"
          className="rounded-2xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/10 via-emerald-500/5 to-transparent p-5 flex items-start gap-4"
        >
          <div className="h-11 w-11 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center shrink-0">
            <PartyPopper size={20} className="text-emerald-300" />
          </div>
          <div className="min-w-0">
            <div className="font-display text-lg text-white">You&apos;re all set — payment received</div>
            <p className="mt-1 text-sm text-slate-300">
              Your <span className="capitalize text-white">{sub?.tier || "subscription"}</span> plan is active. Distribution is unlocked — head to
              <span className="text-white"> My Releases </span>
              to send your first drop to 150+ stores.
            </p>
          </div>
        </div>
      )}

      {/* Current plan banner */}
      <div
        data-testid="billing-current"
        className={`rounded-2xl border p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${
          activeTier ? "border-emerald-500/30 bg-emerald-500/5" : "border-white/10 bg-white/[0.02]"
        }`}
      >
        <div>
          <div className="text-xs tracking-[0.15em] uppercase font-bold text-slate-500">Current plan</div>
          <div className="mt-1 font-display text-2xl text-white capitalize">
            {activeTier ? `${activeTier} plan` : "Free"}
          </div>
          {activeTier && sub?.valid_until && (
            <div className="text-sm text-slate-400 mt-1">Renews {new Date(sub.valid_until).toLocaleDateString()}</div>
          )}
        </div>
        {activeTier && (
          <button
            data-testid="billing-renew"
            onClick={() => upgrade(activeTier)}
            disabled={!!busy}
            className="inline-flex items-center gap-2 rounded-full bg-white text-black font-medium px-5 py-2.5 hover:bg-slate-200 disabled:opacity-60 transition-colors"
          >
            {busy === activeTier ? <Loader2 size={15} className="animate-spin" /> : <>Renew for another year <ExternalLink size={15} /></>}
          </button>
        )}
      </div>

      {/* Plan cards */}
      <div className={`grid grid-cols-1 ${tiers.length > 1 ? "md:grid-cols-2" : ""} gap-5`}>
        {tiers.map((tier) => {
          const p = PLAN_DEFS[tier];
          if (!p) return null;
          const isCurrent = activeTier === tier;
          return (
            <div
              key={tier}
              data-testid={`billing-plan-${tier}`}
              className={`rounded-3xl p-8 border flex flex-col ${
                p.highlight ? "bg-white text-black border-white" : "bg-white/[0.02] text-white border-white/10"
              }`}
            >
              <div className="flex items-center justify-between">
                <h3 className="font-display text-2xl tracking-tight">{p.name}</h3>
                {p.highlight && <span className="text-[10px] tracking-[0.2em] uppercase font-bold bg-blue-600 text-white px-3 py-1 rounded-full">Popular</span>}
              </div>
              <div className="mt-4 flex items-end gap-1">
                <span className="font-display text-4xl font-medium tracking-tight">{p.price}</span>
                <span className={`mb-1.5 text-sm ${p.highlight ? "text-slate-500" : "text-slate-400"}`}>{p.cadence}</span>
              </div>
              {p.billedNote && (
                <div className={`mt-1 text-xs ${p.highlight ? "text-slate-500" : "text-slate-500"}`}>{p.billedNote}</div>
              )}
              <ul className="mt-6 space-y-3 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm">
                    <Check size={18} className={p.highlight ? "text-blue-600 mt-0.5" : "text-blue-400 mt-0.5"} />
                    <span className={p.highlight ? "text-slate-700" : "text-slate-300"}>{f}</span>
                  </li>
                ))}
              </ul>
              <button
                data-testid={`billing-upgrade-${tier}`}
                onClick={() => !isCurrent && upgrade(tier)}
                disabled={isCurrent || busy === tier}
                className={`mt-8 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 font-medium transition-all active:scale-[0.98] disabled:opacity-60 ${
                  p.highlight ? "bg-black text-white hover:bg-slate-800" : "bg-white text-black hover:bg-slate-200"
                }`}
              >
                {busy === tier ? <Loader2 size={18} className="animate-spin" /> : isCurrent ? "Current plan" : `Upgrade to ${p.name}`}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
